import React, {useMemo, useState} from "react";
import {Alert, Card, OverlayTrigger, Popover, Spinner} from "react-bootstrap";
import useStatisticsData from "./useStatisticsData.js";
import {formatEuro, formatQuantity, GROUP_PALETTE, monochromeScale} from "./format.js";
import {formatPallets} from "./inventoryStrategyShared.jsx";

// Drei Abstufungen EINER Farbe statt drei kategorialer Farben - die drei
// Summanden sind eine Zusammensetzung (sie ergeben zusammen exakt den
// Zielbestand), keine unabhängige Kategorienreihe (siehe Auftrag). Index 0
// ist die kräftigste Ausprägung (Bedarf während der Lieferzeit - der
// gesicherte Kern der Empfehlung), Index 2 die hellste (Sicherheitsbestand -
// der Unsicherheitspuffer).
const [BASE_COLOR, CYCLE_COLOR, SAFETY_COLOR] = monochromeScale(GROUP_PALETTE[0], 3, {lightest: 0.75});

const SEGMENTS = [
    {key: "base_demand", label: "Bedarf während der Lieferzeit", color: BASE_COLOR},
    {key: "cycle_demand", label: "Bedarf bis zur nächsten Bestellung", color: CYCLE_COLOR},
    {key: "safety_stock", label: "Sicherheitsbestand", color: SAFETY_COLOR},
];

const SORT_OPTIONS = [
    {value: "target_stock", label: "Zielbestand"},
    {value: "pallets", label: "Paletten"},
];

const LIMIT = 20;

/**
 * Herleitung der Mengenempfehlung - der Vertrauensanker der
 * Lagerstrategie-Seite. Zeigt für die größten Artikel
 * (nach Zielbestand oder Paletten) offen, auf welcher Rechengrundlage die
 * empfohlene Menge beruht: ein waagerechter gestapelter Balken aus den drei
 * Summanden, die zahlengenaue Aufschlüsselung daneben und der vollständige
 * Herleitungssatz (formula_text, vom Server mit den echten Zahlen dieses
 * Artikels gebaut, siehe InventoryStrategyQuery::formatFormulaText())
 * darunter.
 *
 * Rechnet mit denselben Annahmen (params) wie der Rest der Lagerstrategie -
 * Warengruppen/Suche/Zeitraum der globalen Filterleiste fließen hier bewusst
 * NICHT ein (InventoryStrategyController::filtersFromRequest() kennt für
 * diesen Endpunkt nur article_groups/search/level/assumptions, kein
 * date_from/date_to). Wie InventoryKpiCards/InventoryActionList/
 * InventoryGroupPalletsChart geht "params" direkt als Query-Objekt an
 * useStatisticsData - anders als InventoryDetailGrid braucht diese
 * Komponente keine fertig gebaute URL (kein DataGrid mit eigenen
 * Paging-/Sortierparametern dahinter).
 */
export default function InventoryStrategyHerleitung({params}) {
    const [sort, setSort] = useState("target_stock");
    const requestParams = useMemo(() => (params ? {...params, sort, limit: LIMIT} : null), [params, sort]);
    const {data, loading, error} = useStatisticsData("/statistics/inventory-strategy/herleitung", requestParams, {skip: !requestParams});
    const rows = data?.rows || [];

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span className="fw-semibold">Herleitung der Mengenempfehlung</span>
                <div className="btn-group btn-group-sm" role="group" aria-label="Sortierung">
                    {SORT_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={sort === option.value}
                            className={`btn ${sort === option.value ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => setSort(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </Card.Header>
            <Card.Body>
                <p className="small text-muted">
                    Für die {LIMIT} Artikel mit dem größten {sort === "pallets" ? "Palettenbedarf" : "empfohlenen Zielbestand"}: woraus sich die
                    empfohlene Menge zusammensetzt. Die drei Summanden ergeben je Artikel exakt den Zielbestand.
                </p>

                <div className="d-flex flex-wrap align-items-center gap-3 small mb-3">
                    {SEGMENTS.map((segment) => (
                        <span key={segment.key} className="d-inline-flex align-items-center gap-1">
                            <span
                                aria-hidden="true"
                                style={{display: "inline-block", width: 12, height: 12, borderRadius: 2, backgroundColor: segment.color}}
                            />
                            {segment.label}
                        </span>
                    ))}
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading && !data ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Wird geladen…</span>
                        </Spinner>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-muted small text-center py-4">Keine Artikel für die gewählten Rechenannahmen und Filter gefunden.</div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {rows.map((row) => (
                            <HerleitungRow key={row.article_number} row={row} />
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

function HerleitungRow({row}) {
    const total = row.target_stock || 0;

    return (
        <div className="border rounded-2 p-3">
            <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
                <div>
                    <span className="fw-semibold">{row.article_number}</span>
                    {row.name && <span className="text-muted"> · {row.name}</span>}
                    {row.article_group && <span className="text-muted"> · {row.article_group}</span>}
                    {row.abc_class && <span className="badge bg-secondary-subtle text-secondary-emphasis ms-2">Klasse {row.abc_class}</span>}
                </div>
                <div className="text-end">
                    <span className="fw-semibold">{formatQuantity(row.target_stock)} Stück</span>
                    {row.pallets_target !== null && row.pallets_target !== undefined && (
                        <span className="text-muted"> · {formatPallets(row.pallets_target)}</span>
                    )}
                </div>
            </div>

            <div
                className="d-flex overflow-hidden rounded-1"
                style={{height: 22}}
                role="img"
                aria-label={`Zusammensetzung des Zielbestands von ${formatQuantity(row.target_stock)} Stück: ${SEGMENTS.map(
                    (segment) => `${segment.label} ${formatQuantity(row[segment.key])} Stück`
                ).join(", ")}`}
            >
                {SEGMENTS.map((segment) => {
                    const value = row[segment.key] || 0;
                    const widthPct = total > 0 ? (value / total) * 100 : 0;
                    if (widthPct <= 0) {
                        return null;
                    }
                    return (
                        <OverlayTrigger
                            key={segment.key}
                            placement="top"
                            overlay={
                                <Popover>
                                    <Popover.Body className="small py-1 px-2">
                                        {segment.label}: {formatQuantity(value)} Stück
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            {/* trigger bleibt beim Standard (hover + focus), siehe
                                Begründung in inventoryStrategyShared.jsx bei
                                StockByWarehousePopover - "click" zusätzlich in dieses
                                Array kollidiert mit dessen Toggle-Logik. Der onClick
                                fokussiert das Segment stattdessen selbst. */}
                            <div
                                tabIndex={0}
                                role="button"
                                aria-label={`${segment.label}: ${formatQuantity(value)} Stück`}
                                style={{width: `${widthPct}%`, backgroundColor: segment.color, cursor: "help", outline: "none"}}
                                onClick={(event) => event.currentTarget.focus()}
                            />
                        </OverlayTrigger>
                    );
                })}
            </div>

            <div className="small text-muted mt-2">{row.formula_text}</div>
            {row.net_amount_12m !== undefined && (
                <div className="small text-muted mt-1">Absatz 12 Monate: {formatEuro(row.net_amount_12m)}</div>
            )}
        </div>
    );
}
